/**
 * Realtime Diagnostics (TO-468): the operator's view of whether this build's
 * realtime channel is configured and connected, and what it has received this
 * session. SuperAdmin-only. Shows only public `VITE_REALTIME_*` values and the
 * viewer's own ids/topic/messages — never the access token.
 */
import { buildUserTopic, REALTIME_SUBSCRIBE_LEAF, type RealtimeMessage, UserRole } from '@today/types'
import { useMemo } from 'react'
import type { RouteObject } from 'react-router-dom'
import { RequireRole } from '@/components/RequireRole'
import { useAuth } from '@/hooks/useAuth'
import {
	describeRealtimeEnv,
	type RealtimeEnvField,
	RealtimeEnvFieldStatus,
	readRealtimeConfig,
} from '@/realtime/realtime-config'
import {
	RealtimeConnectionState,
	useRealtimeConnectionState,
	useRealtimeMessageLog,
} from '@/realtime/realtime-dispatcher'
import { realtimeEnv } from '@/realtime/realtime-env'

const show = (f: RealtimeEnvField): string => {
	if (f.status === RealtimeEnvFieldStatus.MISSING) return 'missing'
	if (f.status === RealtimeEnvFieldStatus.MALFORMED) return `malformed: ${f.value ?? ''}`
	return f.value ?? ''
}

function Row({ label, testId, value }: { label: string; testId: string; value: string }) {
	return (
		<div className="grid grid-cols-3 gap-4 py-2 border-b border-ds-border">
			<dt className="text-sm text-ds-text-muted">{label}</dt>
			<dd data-testid={testId} className="col-span-2 text-sm font-mono text-ds-text break-all">
				{value}
			</dd>
		</div>
	)
}

/** Configuration, the viewer's own topic filter and the live state, for the page. */
function useRealtimeDiagnostics() {
	const { user } = useAuth()
	const { env, config } = useMemo(() => {
		const e = realtimeEnv()
		return { env: describeRealtimeEnv(e), config: readRealtimeConfig(e) }
	}, [])
	const liveState = useRealtimeConnectionState()
	const state = config ? liveState : RealtimeConnectionState.DISABLED
	const filter =
		config && user?.tenantId && user.userId
			? buildUserTopic({
					app: config.app,
					stage: config.stage,
					tenantId: user.tenantId,
					userId: user.userId,
					entityType: REALTIME_SUBSCRIBE_LEAF,
				})
			: '—'
	return { env, configured: config !== null, state, filter }
}

function MessageLog({ messages }: { messages: readonly RealtimeMessage[] }) {
	if (messages.length === 0) return <p className="text-sm text-ds-text-muted">No messages received yet.</p>
	return (
		<table aria-label="Received this session" className="w-full text-sm">
			<thead>
				<tr className="text-left text-ds-text-muted">
					<th className="py-1">Type</th>
					<th className="py-1">Entity</th>
					<th className="py-1">Timestamp</th>
				</tr>
			</thead>
			<tbody>
				{messages.map((m, i) => (
					// Messages carry no id; the log is newest-first and only ever prepended.
					// biome-ignore lint/suspicious/noArrayIndexKey: position is the identity in this bounded log.
					<tr key={`${m.timestamp}-${i}`} className="border-t border-ds-border font-mono">
						<td className="py-1">{m.type}</td>
						<td className="py-1">{`${m.entityRef.type}:${m.entityRef.id}`}</td>
						<td className="py-1">{m.timestamp}</td>
					</tr>
				))}
			</tbody>
		</table>
	)
}

export function RealtimeDiagnosticsPage() {
	const { env, configured, state, filter } = useRealtimeDiagnostics()
	const messages = useRealtimeMessageLog()

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-2xl font-bold text-ds-text">Realtime diagnostics</h1>
				<p className="text-sm text-ds-text-muted mt-1">
					{configured ? 'Realtime push channel for this build.' : 'Realtime not configured — polling fallback.'}
				</p>
			</div>

			<dl className="rounded-lg border border-ds-border bg-ds-surface px-4">
				<Row label="Endpoint" testId="realtime-endpoint" value={show(env.endpoint)} />
				<Row label="Authorizer" testId="realtime-authorizer" value={show(env.authorizer)} />
				<Row label="Topic prefix" testId="realtime-prefix" value={show(env.topicPrefix)} />
				<Row label="Your topic filter" testId="realtime-filter" value={filter} />
				<Row label="Connection state" testId="realtime-state" value={state} />
			</dl>

			<section>
				<h2 className="text-lg font-semibold text-ds-text mb-2">Received this session</h2>
				<MessageLog messages={messages} />
			</section>
		</div>
	)
}

/** `/admin/realtime` — SuperAdmin-only, mounted under the `/admin` route tree. */
export const realtimeDiagnosticsRoute: RouteObject = {
	path: 'realtime',
	element: (
		<RequireRole anyOf={[UserRole.SUPER_ADMIN]}>
			<RealtimeDiagnosticsPage />
		</RequireRole>
	),
}
